/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { CheckCircledIcon } from '@radix-ui/react-icons';
import { IModalHeader } from 'types/modal';

import { DialogTitle } from '@/components/ui/dialog';

import { Logo } from '../cards';
import { Icons } from '../icons';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

export const ModalHeader = ({ children, modalProps }: IModalHeader) => {
  const {
    typeHead,
    type,
    displayTitle,
    componentName,
    providerData,
    autoDiscovered = false,
    functionName,
    isDefault,
    idValue,
    isService,
    status,
    showDeleteIcon,
    handleDeleteOrArchive,
    typeName,
    isComponent,
  } = modalProps;

  const [copied, setCopied] = useState(false);
  const { messages } = useLanguage();
  const generalIntl = messages?.General;
  const langData = messages?.ProviderServiceEditDetailsDialog;

  const truncatedId = idValue
    ? `${idValue.slice(0, 10)}${idValue.length > 10 ? '...' : ''}`
    : '';

  const handleCopy = () => {
    if (!copied) {
      navigator.clipboard.writeText(idValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <div className="mb-4 flex h-fit items-center gap-2">
        <div className="flex items-center justify-center self-start">
          <Logo
            type={type ? type : ''}
            className="border border-[#40565A]"
            size={{ img: 'size-10', bg: 'size-16' }}
          />
        </div>
        <div className="mr-8 flex w-full justify-between">
          <div className="flex w-full flex-col justify-center">
            <div className="flex items-center gap-3">
              {typeName && (
                <>
                  <span className="text-sm text-body-subtle">{typeName}</span>
                  <div className="h-4 border-l border-form-control-border-rest opacity-[24%]" />
                </>
              )}

              {typeHead && (
                <span className="text-sm text-body-subtle">{typeHead}</span>
              )}

              {componentName && (
                <p className="text-form-input-text text-body-subtle">
                  {componentName}
                </p>
              )}

              {status === 'active' && (
                <div className="flex items-center gap-3">
                  <div className="h-4 border-l border-form-control-border-rest opacity-[24%]" />
                  <p className="flex items-center gap-1 text-form-input-text text-body-subtle">
                    <CheckCircledIcon className="h-[18px] w-[18px] text-[#57A300]" />
                    {generalIntl?.active}
                  </p>
                </div>
              )}
              {status === 'archived' && (
                <div className="flex items-center gap-3">
                  <div className="h-4 border-l border-form-control-border-rest opacity-[24%]" />
                  <p className="flex items-center gap-1 text-form-input-text text-body-subtle">
                    {Icons.ArchiveIcon('size-[18px] text-[#FFCB78]')}
                    {generalIntl?.archived}
                  </p>
                </div>
              )}
            </div>
            <DialogTitle className="mb-1 flex justify-between font-normal">
              <div className="flex items-center gap-4">
                <p className="max-w-[470px] truncate text-2xl capitalize">
                  {displayTitle}
                </p>
                {autoDiscovered && (
                  <div className="flex items-center gap-3">
                    <div className="flex h-6 items-center gap-1 rounded-3xl bg-[#257C9D] pl-2 pr-3 text-body-sm text-white">
                      {Icons.AutoDetected()}
                      {messages?.General?.auto_detected}
                    </div>
                  </div>
                )}
              </div>

              <div className="absolute right-[62px] top-[29px] cursor-pointer">
                {isComponent
                  ? (status === 'active' || status === 'archived') && (
                      <>
                        {status === 'active' ? (
                          <span onClick={handleDeleteOrArchive}>
                            {Icons.ArchiveIcon('size-6 text-[#257C9D]')}
                          </span>
                        ) : (
                          showDeleteIcon && (
                            <span onClick={handleDeleteOrArchive}>
                              {Icons.TrashIcon('size-6')}
                            </span>
                          )
                        )}
                      </>
                    )
                  : status === 'active' && (
                      <>
                        {showDeleteIcon ? (
                          <span onClick={handleDeleteOrArchive}>
                            {Icons.TrashIcon('size-6')}
                          </span>
                        ) : (
                          <span onClick={handleDeleteOrArchive}>
                            {Icons.ArchiveIcon('size-6 text-[#257C9D]')}
                          </span>
                        )}
                      </>
                    )}
              </div>
            </DialogTitle>

            <div
              className={`flex ${providerData && 'min-h-[32px]'} flex-wrap items-center gap-3`}
            >
              {isDefault && (
                <div className="flex items-center gap-3">
                  <span className="flex h-6 items-center justify-center rounded-3xl border border-white/[24%] pb-[1px] pl-2 pr-[10px] text-[11px] tracking-[2%]">
                    {generalIntl?.default}
                  </span>
                  <div className="h-4 border-l border-form-control-border-rest opacity-[24%]" />
                </div>
              )}

              {providerData?.display_name && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Logo
                      type={providerData?.type}
                      className="border border-[#40565A]"
                      size={{ img: 'size-5', bg: 'size-8' }}
                    />
                    <span className="text-form-input-text text-body-subtle">
                      {providerData?.display_name}
                    </span>
                  </div>
                  <div className="h-4 border-l border-form-control-border-rest opacity-[24%]" />
                </div>
              )}

              {functionName && (
                <div className="flex items-center gap-3">
                  <p className="text-form-input-text text-body-subtle">
                    {generalIntl?.function}: {functionName}
                  </p>
                  <div className="h-4 border-l border-form-control-border-rest opacity-[24%]" />
                </div>
              )}

              {idValue && (
                <div className="flex flex-nowrap items-center gap-1.5">
                  {!isService ? (
                    <p className="text-form-input-text text-body-subtle">
                      {generalIntl?.id}: {idValue}
                    </p>
                  ) : (
                    <>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-default text-form-input-text text-body-subtle">
                              {generalIntl?.id}: {truncatedId}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="w-48 rounded p-[9px] text-[11px] leading-4 tracking-[2%] text-white">
                            {langData?.id_tooltip_text}
                            <TooltipArrow className="fill-[#07181E]" />
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {isService && (
                        <>
                          {copied ? (
                            <div className="flex items-center gap-0.5 text-[11px] leading-4 tracking-[2%] text-[#8C9EA1]">
                              <CheckCircledIcon className="h-4 w-4 text-[#57A300]" />
                              <p className="text-body-sm tracking-[2%] text-[#57A300]">
                                {generalIntl?.copied}
                              </p>
                            </div>
                          ) : (
                            <div
                              onClick={handleCopy}
                              className="mr-0.5 cursor-pointer text-[#257C9D]"
                            >
                              {Icons.Copy('h-4 w-4')}
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {children}
    </>
  );
};
